import { useEffect, useRef } from "react";

// CAPTCHA fully disabled (2026-02-11) — users were hitting reCAPTCHA errors on
// production browsers. We keep the file so imports don't break, but the key is
// forced empty which makes every downstream check (Recaptcha component render,
// useAuthFlow captchaRequired, token-send) short-circuit to disabled.
export const RECAPTCHA_SITE_KEY = "";

let scriptPromise = null;
function loadScript() {
    if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("recaptcha load failed"));
        document.head.appendChild(s);
    });
    return scriptPromise;
}

/**
 * Google reCAPTCHA v2 "I'm not a robot" checkbox.
 * Renders nothing if no site key is configured (feature stays optional).
 * `onChange(token)` fires with the solved token (or "" on expiry/error).
 * Bump `resetSignal` (a number) to clear+reset the widget after a submit.
 */
export function Recaptcha({ onChange, resetSignal = 0 }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY) return;
        let cancelled = false;
        loadScript()
            .then(() => {
                const tryRender = () => {
                    if (cancelled) return;
                    if (
                        window.grecaptcha &&
                        window.grecaptcha.render &&
                        containerRef.current &&
                        widgetIdRef.current === null
                    ) {
                        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
                            sitekey: RECAPTCHA_SITE_KEY,
                            callback: (token) => onChangeRef.current?.(token),
                            "expired-callback": () => onChangeRef.current?.(""),
                            "error-callback": () => onChangeRef.current?.(""),
                        });
                    } else if (widgetIdRef.current === null) {
                        setTimeout(tryRender, 120);
                    }
                };
                tryRender();
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (resetSignal > 0 && widgetIdRef.current !== null && window.grecaptcha) {
            try {
                window.grecaptcha.reset(widgetIdRef.current);
            } catch (_) {
                /* noop */
            }
            onChangeRef.current?.("");
        }
    }, [resetSignal]);

    if (!RECAPTCHA_SITE_KEY) return null;
    return (
        <div
            ref={containerRef}
            data-testid="auth-recaptcha"
            className="flex justify-center min-h-[78px]"
        />
    );
}
